import { useState } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { FolderKanban, Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { useSimpleRole } from "@/hooks/use-simple-role";
import { useUserOrganization } from "@/hooks/use-user-organization";
import {
  useOrganizationScopedDeviceGroupsWithCounts,
  useCreateDeviceGroup,
  useUpdateDeviceGroup,
  useDeleteDeviceGroup,
  useManageableOrganisations,
  DeviceGroup,
} from "@/hooks/use-device-groups";
import { format } from "date-fns";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { canManageGroupOrg } from "@/utils/deviceGroupAccess";
import { sortOrganisationHierarchy } from "@/utils/organisationSort";

const DeviceGroups = () => {
  const { role } = useSimpleRole();
  const { data: userOrgId, isLoading: isOrgLoading } = useUserOrganization();
  const isSuperuser = role === 'superuser';
  const isAdmin = role === 'admin';
  const canAccessPage = isSuperuser || isAdmin;
  
  const { data: orgs = [] } = useManageableOrganisations();
  // "all" = overview; otherwise the organisation whose groups are shown/created
  const [selectedOrg, setSelectedOrg] = useState<string>("all");
  const childOrgIds = orgs.filter((o) => o.id !== userOrgId).map((o) => o.id);
  const orgName = (id: string | null) => orgs.find((o) => o.id === id)?.name ?? "—";
  const sortedOrgs = sortOrganisationHierarchy(orgs);
  const createOrgId = selectedOrg !== "all" ? selectedOrg : (isSuperuser ? null : userOrgId);
  const canCreate = canManageGroupOrg(role, userOrgId, childOrgIds, createOrgId);
  const showOrgPicker = isSuperuser || orgs.length > 1;

  const { data: groups = [], isLoading } = useOrganizationScopedDeviceGroupsWithCounts(
    selectedOrg !== "all" ? selectedOrg : null
  );
  const createGroup = useCreateDeviceGroup();
  const updateGroup = useUpdateDeviceGroup();
  const deleteGroup = useDeleteDeviceGroup();

  const [editingGroup, setEditingGroup] = useState<DeviceGroup | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<DeviceGroup | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Form state
  const [formName, setFormName] = useState("");
  const [formReference, setFormReference] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formColor, setFormColor] = useState("#6366f1");

  const resetForm = () => {
    setFormName("");
    setFormReference("");
    setFormDescription("");
    setFormColor("#6366f1");
  };

  const openEditDialog = (group: DeviceGroup) => {
    setEditingGroup(group);
    setFormName(group.name);
    setFormReference(group.reference || "");
    setFormDescription(group.description || "");
    setFormColor(group.color || "#6366f1");
  };

  const handleCreate = async () => {
    if (!formName.trim() || !canCreate) return;

    try {
      await createGroup.mutateAsync({
        name: formName.trim(),
        organization_id: createOrgId,
        reference: formReference.trim() || null,
        description: formDescription.trim() || null,
        color: formColor || null,
      });
      setCreateDialogOpen(false);
      resetForm();
    } catch {
      // Error handled in hook
    }
  };

  const handleUpdate = async () => {
    if (!editingGroup || !formName.trim()) return;

    try {
      await updateGroup.mutateAsync({
        id: editingGroup.id,
        name: formName.trim(),
        reference: formReference.trim() || null,
        description: formDescription.trim() || null,
        color: formColor || null,
      });
      setEditingGroup(null);
      resetForm();
    } catch {
      // Error handled in hook
    }
  };

  const handleDelete = async () => {
    if (!deletingGroup) return;

    try {
      await deleteGroup.mutateAsync(deletingGroup.id);
      setDeletingGroup(null);
    } catch {
      // Error handled in hook
    }
  };

  const isLoadingRole = !role || (isAdmin && isOrgLoading);

  if (isLoadingRole) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!canAccessPage) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-muted-foreground">Access denied. Admin or superuser access required.</p>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        <main className="flex-1 overflow-auto">
          <FixedPageHeader
            title="Device Groups"
            description="Manage device groups for organizing your devices"
            icon={FolderKanban}
            actions={
              <div className="flex items-center gap-2">
                {showOrgPicker && (
                  <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                    <SelectTrigger className="w-[240px]" aria-label="Organisation">
                      <SelectValue placeholder="Organisation" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{isSuperuser ? "All organisations" : "All my organisations"}</SelectItem>
                      {sortedOrgs.map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          {o.parent_organization_id ? `↳ ${o.name}` : o.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button
                  onClick={() => setCreateDialogOpen(true)}
                  disabled={!canCreate}
                  title={!canCreate ? "Choose an organisation first" : undefined}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Group
                </Button>
              </div>
            }
          />

          <div className="p-4 md:p-6">
            <Card>
              <CardHeader>
                <CardTitle>
                  {selectedOrg !== "all"
                    ? `${orgName(selectedOrg)} groups`
                    : isSuperuser ? "All Groups (All Organizations)" : "Your Organization's Groups"}
                </CardTitle>
                <CardDescription>
                  {groups.length} group{groups.length !== 1 ? "s" : ""} configured
                  {isSuperuser && selectedOrg === "all" && " across all organizations"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : groups.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <FolderKanban className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No device groups created yet.</p>
                    <p className="text-sm">Create your first group to start organizing devices.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Color</TableHead>
                        <TableHead>Name</TableHead>
                        {selectedOrg === "all" && showOrgPicker && <TableHead>Organisation</TableHead>}
                        <TableHead>Reference</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-center">Devices</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {groups.map((group) => (
                        <TableRow key={group.id}>
                          <TableCell>
                            {group.color && (
                              <div
                                className="w-6 h-6 rounded-full border"
                                style={{ backgroundColor: group.color }}
                              />
                            )}
                          </TableCell>
                          <TableCell className="font-medium">{group.name}</TableCell>
                          {selectedOrg === "all" && showOrgPicker && (
                            <TableCell>
                              <Badge variant="outline">{orgName(group.organization_id)}</Badge>
                            </TableCell>
                          )}
                          <TableCell className="text-muted-foreground">
                            {group.reference || "-"}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate text-muted-foreground">
                            {group.description || "-"}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary">{group.device_count || 0}</Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {format(new Date(group.created_at), "MMM d, yyyy")}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEditDialog(group)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeletingGroup(group)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Group</DialogTitle>
            <DialogDescription>
              {createOrgId
                ? `This group will belong to ${orgName(createOrgId)}.`
                : "Choose an organisation first."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="create-name">Group Name *</Label>
              <Input
                id="create-name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g., Main Entrance"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-reference">Reference</Label>
              <Input
                id="create-reference"
                value={formReference}
                onChange={(e) => setFormReference(e.target.value)}
                placeholder="e.g., GRP-001"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-description">Description</Label>
              <Textarea
                id="create-description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Brief description"
                className="min-h-[60px]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-color">Color</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="create-color"
                  type="color"
                  value={formColor}
                  onChange={(e) => setFormColor(e.target.value)}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  value={formColor}
                  onChange={(e) => setFormColor(e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialogOpen(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={!formName.trim() || createGroup.isPending}>
              {createGroup.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingGroup} onOpenChange={() => { setEditingGroup(null); resetForm(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Group</DialogTitle>
            <DialogDescription>Update the device group details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Group Name *</Label>
              <Input
                id="edit-name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g., Main Entrance"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-reference">Reference</Label>
              <Input
                id="edit-reference"
                value={formReference}
                onChange={(e) => setFormReference(e.target.value)}
                placeholder="e.g., GRP-001"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Brief description"
                className="min-h-[60px]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-color">Color</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="edit-color"
                  type="color"
                  value={formColor}
                  onChange={(e) => setFormColor(e.target.value)}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  value={formColor}
                  onChange={(e) => setFormColor(e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditingGroup(null); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={!formName.trim() || updateGroup.isPending}>
              {updateGroup.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingGroup} onOpenChange={() => setDeletingGroup(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Group</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingGroup?.name}"?
              {(deletingGroup?.device_count || 0) > 0 && (
                <span className="block mt-2 text-amber-600">
                  This group has {deletingGroup?.device_count} device(s) assigned. 
                  They will be unassigned from this group.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteGroup.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarProvider>
  );
};

export default DeviceGroups;
